import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { XLSX_TYPE } from '@mahallu/shared';
import type {
  AccessGrant,
  AccessInvite,
  AccessUser,
  AuthErrorCode,
  AnnouncementInput,
  AppSettings,
  AppSettingsPatch,
  AuditList,
  ConsoleAuditRow,
  ConsoleLead,
  ConsoleMe,
  ConsoleOperator,
  ConsoleOverview,
  ConsoleTenant,
  LeadStatus,
  PublicMahallu,
  MahalluProfile,
  ProfilePatch,
  AnnouncementRow,
  CertRequestInput,
  InboxItem,
  JanazaInput,
  NotificationList,
  CertRequestRow,
  CertificateRow,
  ConsentInput,
  PublicCertificate,
  MessageList,
  MessageRow,
  MessageTemplateRow,
  DuesMonth,
  Fund,
  FundInput,
  HouseAccount,
  RaiseResult,
  RatePlanPatch,
  RatePlanRow,
  ReceiptInput,
  ReceiptList,
  ReceiptRow,
  HouseConsents,
  HealthResult,
  HouseCreate,
  ImportResult,
  HouseDetail,
  HousePatch,
  HouseList,
  HouseSummary,
  MeResponse,
  MemberInput,
  MemberPatch,
  OtpChannel,
  OfficerInput,
  OtpStartResponse,
  PrivacyRequest,
  PrivacyRequestInput,
  PrivacySettings,
  PublicPrivacy,
  Role,
  SequenceReset,
  SequenceRow,
  SessionRow,
  SignInStepResponse,
  TotpSetupResponse,
  Ward,
  WardInput,
} from '@mahallu/shared';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly code?: AuthErrorCode,
    public readonly attemptsLeft?: number,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, { credentials: 'same-origin', ...init, headers: { Accept: 'application/json', ...init?.headers } });
  const body = (await res.json().catch(() => ({}))) as { error?: string; code?: AuthErrorCode; attemptsLeft?: number };
  if (!res.ok) throw new ApiError(res.status, body.error ?? `Request failed (${res.status})`, body.code ?? (res.status === 429 ? 'RATE_LIMITED' : undefined), body.attemptsLeft);
  return body as T;
}

const getJson = <T>(path: string) => request<T>(path);

/** The message for a failed load: signed out and no access are not "the API is down". */
export function loadErrorKey(error: unknown): 'table.signedOut' | 'table.forbidden' | 'table.error' {
  if (error instanceof ApiError && (error.status === 401 || error.code === 'CHOOSE_MAHALLU')) return 'table.signedOut';
  if (error instanceof ApiError && error.status === 403) return 'table.forbidden';
  return 'table.error';
}

/** Client errors (4xx) are answers, not outages: retrying them only delays the right screen. */
export function shouldRetry(failures: number, error: unknown): boolean {
  return !(error instanceof ApiError && error.status < 500) && failures < 1;
}
const sendJson = <T>(method: 'POST' | 'PATCH' | 'PUT', path: string, body: unknown = {}) =>
  request<T>(path, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
const postJson = <T>(path: string, body: unknown = {}) => sendJson<T>('POST', path, body);

/** Builds a query string from filters, dropping empty values. */
export function toQuery(params: Record<string, string | number | undefined>): string {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== '') qs.set(k, String(v));
  const s = qs.toString();
  return s ? `?${s}` : '';
}

// ── Sign-in ──
export const auth = {
  start: (identifier: string, channel?: OtpChannel) => postJson<OtpStartResponse>('/api/auth/otp/start', channel ? { identifier, channel } : { identifier }),
  verify: (challengeId: string, code: string) => postJson<SignInStepResponse>('/api/auth/otp/verify', { challengeId, code }),
  chooseMahallu: (slug: string) => postJson<SignInStepResponse>('/api/auth/mahallu', { slug }),
  totpSetup: () => postJson<TotpSetupResponse>('/api/auth/mfa/totp/setup'),
  totpVerify: (code: string) => postJson<SignInStepResponse>('/api/auth/mfa/totp/verify', { code }),
  logout: () => postJson<{ status: string }>('/api/auth/logout'),
  switchRole: (role: Role) => postJson<SignInStepResponse>('/api/auth/role', { role }),
  endSession: (id: string) => postJson<{ ended: number; current: boolean }>(`/api/auth/sessions/${encodeURIComponent(id)}/end`),
  endOtherSessions: () => postJson<{ ended: number }>('/api/auth/sessions/end-others'),
};

export function useSessions() {
  return useQuery({ queryKey: ['sessions'], queryFn: () => getJson<SessionRow[]>('/api/auth/sessions') });
}

// ── Users & roles ──
export function useAccessUsers() {
  return useQuery({ queryKey: ['access'], queryFn: () => getJson<AccessUser[]>('/api/access/users') });
}

const accessPath = (id: string) => `/api/access/users/${encodeURIComponent(id)}`;
export const access = {
  invite: (body: AccessInvite) => postJson<AccessUser>('/api/access/users', body),
  grant: (id: string, body: AccessGrant) => postJson<AccessUser>(`${accessPath(id)}/roles`, body),
  removeGrant: (id: string, grantId: string) => request<AccessUser>(`${accessPath(id)}/roles/${encodeURIComponent(grantId)}`, { method: 'DELETE' }),
  setStatus: (id: string, status: AccessUser['status']) => postJson<AccessUser>(`${accessPath(id)}/status`, { status }),
  resetAuthenticator: (id: string) => postJson<AccessUser>(`${accessPath(id)}/reset-authenticator`),
};

export function useMe() {
  return useQuery({ queryKey: ['me'], queryFn: () => getJson<MeResponse>('/api/auth/me'), retry: false, staleTime: 60_000 });
}

// ── Register ──
export interface HouseFilterParams {
  ward?: string;
  status?: string;
  residence?: string;
  q?: string;
}

export function useWards() {
  return useQuery({ queryKey: ['wards'], queryFn: () => getJson<Ward[]>('/api/wards') });
}

export function useHouseSummary(filters: HouseFilterParams) {
  return useQuery({
    queryKey: ['houses', 'summary', filters],
    queryFn: () => getJson<HouseSummary>(`/api/houses/summary${toQuery({ ...filters })}`),
    placeholderData: keepPreviousData,
  });
}

export function useHouses(filters: HouseFilterParams & { tile?: string; page: number; size: number }) {
  return useQuery({
    queryKey: ['houses', 'list', filters],
    queryFn: () => getJson<HouseList>(`/api/houses${toQuery({ ...filters })}`),
    placeholderData: keepPreviousData,
  });
}

export function useHouse(id: string | null) {
  return useQuery({
    queryKey: ['houses', 'detail', id],
    queryFn: () => getJson<HouseDetail>(`/api/houses/${encodeURIComponent(id!)}`),
    enabled: Boolean(id),
  });
}

// ── Sequence Master (settings) ──
export function useSequences() {
  return useQuery({ queryKey: ['sequences'], queryFn: () => getJson<SequenceRow[]>('/api/sequences') });
}

export const sequences = {
  update: (code: string, change: { pattern?: string; reset?: SequenceReset; version: number }) =>
    sendJson<SequenceRow>('PATCH', `/api/sequences/${encodeURIComponent(code)}`, change),
  raiseNext: (code: string, nextValue: number, ward?: string) =>
    sendJson<SequenceRow>('PUT', `/api/sequences/${encodeURIComponent(code)}/next`, ward ? { ward, nextValue } : { nextValue }),
};

// ── Editing the register (pilot slice 3) ──
export const register = {
  createHouse: (body: HouseCreate) => sendJson<HouseDetail>('POST', '/api/houses', body),
  updateHouse: (id: string, body: HousePatch) => sendJson<HouseDetail>('PATCH', `/api/houses/${encodeURIComponent(id)}`, body),
  addMember: (houseId: string, body: MemberInput) => sendJson<HouseDetail>('POST', `/api/houses/${encodeURIComponent(houseId)}/members`, body),
  updateMember: (id: string, body: MemberPatch) => sendJson<HouseDetail>('PATCH', `/api/members/${encodeURIComponent(id)}`, body),
  createWard: (body: WardInput) => sendJson<Ward>('POST', '/api/wards', body),
  updateWard: (code: string, body: Partial<Omit<WardInput, 'code'>>) => sendJson<Ward>('PATCH', `/api/wards/${encodeURIComponent(code)}`, body),
};

/** Query keys a register write can change: lists, tiles, the open house, wards and next numbers. */
export const REGISTER_KEYS = [['houses'], ['wards'], ['sequences']] as const;

// ── Privacy (pilot slice 5) ──
export function usePrivacySettings() {
  return useQuery({ queryKey: ['privacy', 'settings'], queryFn: () => getJson<PrivacySettings>('/api/privacy/settings') });
}

export function usePrivacyRequests(status: 'open' | 'closed' | 'all') {
  return useQuery({ queryKey: ['privacy', 'requests', status], queryFn: () => getJson<PrivacyRequest[]>(`/api/privacy/requests${toQuery({ status })}`) });
}

export function useHouseConsents(houseId: string | null) {
  return useQuery({
    queryKey: ['houses', 'consents', houseId],
    queryFn: () => getJson<HouseConsents>(`/api/houses/${encodeURIComponent(houseId!)}/consents`),
    enabled: Boolean(houseId),
  });
}

export function usePublicPrivacy(slug: string) {
  return useQuery({ queryKey: ['public', 'privacy', slug], queryFn: () => getJson<PublicPrivacy>(`/api/public/privacy/${encodeURIComponent(slug)}`), retry: false });
}

const requestPath = (id: string) => `/api/privacy/requests/${encodeURIComponent(id)}`;
export const privacy = {
  setOfficer: (body: OfficerInput) => sendJson<PrivacySettings>('PUT', '/api/privacy/officer', body),
  saveDraft: (body: { bodyEn: string; bodyMl: string; version?: number }) => sendJson<PrivacySettings>('PUT', '/api/privacy/notice/draft', body),
  publish: (version: number) => postJson<PrivacySettings>('/api/privacy/notice/publish', { version }),
  setRetention: (record: string, months: number, version: number | null) =>
    sendJson<PrivacySettings>('PUT', `/api/privacy/retention/${encodeURIComponent(record)}`, version === null ? { months } : { months, version }),
  recordConsent: (houseId: string, body: ConsentInput) => postJson<HouseConsents>(`/api/houses/${encodeURIComponent(houseId)}/consents`, body),
  createRequest: (body: PrivacyRequestInput) => postJson<PrivacyRequest>('/api/privacy/requests', body),
  completeRequest: (id: string, version: number, note: string | null) => postJson<PrivacyRequest>(`${requestPath(id)}/complete`, { version, note }),
  rejectRequest: (id: string, version: number, note: string) => postJson<PrivacyRequest>(`${requestPath(id)}/reject`, { version, note }),
};

// ── Register spreadsheet (pilot slice 6) ──
/**
 * Downloads a file from the API and hands it to the browser to save. A refusal arrives as JSON and is
 * thrown as an ApiError, so the screen can say why instead of saving an error page.
 */
export async function downloadFile(path: string, fallbackName: string): Promise<void> {
  const res = await fetch(path, { credentials: 'same-origin' });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string; code?: AuthErrorCode };
    throw new ApiError(res.status, body.error ?? `Request failed (${res.status})`, body.code);
  }
  const name = /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '')?.[1] ?? fallbackName;
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export const registerSheet = {
  template: () => downloadFile('/api/register/template', 'Rabita_RegisterImportTemplate.xlsx'),
  export: (filters: Record<string, string | number | undefined>) => downloadFile(`/api/register/export${toQuery(filters)}`, 'Rabita_Register.xlsx'),
  /** Check or apply. Both answer with the full result; an apply refused for problems (422) does too. */
  import: async (mode: 'check' | 'apply', file: File): Promise<ImportResult> => {
    const res = await fetch(`/api/register/import?mode=${mode}`, { method: 'POST', credentials: 'same-origin', headers: { Accept: 'application/json', 'Content-Type': XLSX_TYPE }, body: file });
    const body = (await res.json().catch(() => ({}))) as ImportResult & { error?: string; code?: AuthErrorCode };
    if (res.ok || res.status === 422) return body;
    throw new ApiError(res.status, body.error ?? `Request failed (${res.status})`, body.code);
  },
};

export function useDataHealth() {
  return useQuery({ queryKey: ['houses', 'health'], queryFn: () => getJson<HealthResult[]>('/api/register/health') });
}

// ── Varisankhya (pilot slice 7) ──
export const MONEY_KEYS = [['money'], ['houses']] as const;

export function useRatePlans() {
  return useQuery({ queryKey: ['money', 'plans'], queryFn: () => getJson<RatePlanRow[]>('/api/varisankhya/plans') });
}
export function useFunds() {
  return useQuery({ queryKey: ['money', 'funds'], queryFn: () => getJson<Fund[]>('/api/varisankhya/funds') });
}
export function useDuesMonth(period: string) {
  return useQuery({ queryKey: ['money', 'dues', period], queryFn: () => getJson<DuesMonth>(`/api/varisankhya/dues${toQuery({ period })}`) });
}
export function useHouseAccount(houseId: string | null) {
  return useQuery({
    queryKey: ['money', 'account', houseId],
    queryFn: () => getJson<HouseAccount>(`/api/varisankhya/houses/${encodeURIComponent(houseId!)}/account`),
    enabled: Boolean(houseId),
  });
}
export function useReceipts(filters: Record<string, string | number | undefined>) {
  return useQuery({ queryKey: ['money', 'receipts', filters], queryFn: () => getJson<ReceiptList>(`/api/varisankhya/receipts${toQuery(filters)}`), placeholderData: keepPreviousData });
}
export function useReceipt(id: string) {
  return useQuery({ queryKey: ['money', 'receipt', id], queryFn: () => getJson<ReceiptRow>(`/api/varisankhya/receipts/${encodeURIComponent(id)}`) });
}

export const money = {
  updatePlan: (code: string, body: RatePlanPatch) => sendJson<RatePlanRow[]>('PATCH', `/api/varisankhya/plans/${encodeURIComponent(code)}`, body),
  createFund: (body: FundInput) => postJson<Fund[]>('/api/varisankhya/funds', body),
  updateFund: (code: string, body: { nameEn?: string; nameMl?: string | null; active?: boolean; version: number }) =>
    sendJson<Fund[]>('PATCH', `/api/varisankhya/funds/${encodeURIComponent(code)}`, body),
  raiseDues: (period: string) => postJson<RaiseResult>('/api/varisankhya/dues/raise', { period }),
  issueReceipt: (body: ReceiptInput) => postJson<ReceiptRow>('/api/varisankhya/receipts', body),
  cancelReceipt: (id: string, reason: string, version: number) => postJson<ReceiptRow>(`/api/varisankhya/receipts/${encodeURIComponent(id)}/cancel`, { reason, version }),
};

// ── Messages (pilot slice 8) ──
export function useMessages(filters: Record<string, string | number | undefined>) {
  return useQuery({ queryKey: ['messages', 'list', filters], queryFn: () => getJson<MessageList>(`/api/messages${toQuery(filters)}`), placeholderData: keepPreviousData });
}
export function useMessageTemplates() {
  return useQuery({ queryKey: ['messages', 'templates'], queryFn: () => getJson<MessageTemplateRow[]>('/api/messages/templates') });
}
export const messages = {
  retry: (id: string, version: number) => postJson<MessageRow>(`/api/messages/${encodeURIComponent(id)}/retry`, { version }),
};

// ── Certificates (pilot slice 9) ──
export function useCertRequests(state: string) {
  return useQuery({ queryKey: ['certs', 'requests', state], queryFn: () => getJson<CertRequestRow[]>(`/api/certificates/requests${toQuery({ state })}`) });
}
export function useIssuedCertificates() {
  // A PDF is printed by the worker a few seconds after issue; look again while any is waiting.
  return useQuery({
    queryKey: ['certs', 'issued'],
    queryFn: () => getJson<CertificateRow[]>('/api/certificates/issued'),
    refetchInterval: (q) => (q.state.data?.some((c) => c.pdf.state === 'PENDING') ? 4000 : false),
  });
}
export function usePublicCertificate(token: string) {
  return useQuery({ queryKey: ['public', 'cert', token], queryFn: () => getJson<PublicCertificate>(`/api/public/certificates/${encodeURIComponent(token)}`), retry: false });
}
export const certs = {
  request: (body: CertRequestInput) => postJson<CertRequestRow>('/api/certificates/requests', body),
  sign: (id: string, version: number) => postJson<CertRequestRow>(`/api/certificates/requests/${encodeURIComponent(id)}/sign`, { version }),
  reject: (id: string, reason: string, version: number) => postJson<CertRequestRow>(`/api/certificates/requests/${encodeURIComponent(id)}/reject`, { reason, version }),
  withdraw: (id: string, reason: string, version: number) => postJson<CertificateRow>(`/api/certificates/issued/${encodeURIComponent(id)}/withdraw`, { reason, version }),
  pdf: (id: string, serial: string) => downloadFile(`/api/certificates/issued/${encodeURIComponent(id)}/pdf`, `${serial}.pdf`),
};

// ── Announcements, notifications, inbox (pilot slice 10) ──
export function useAnnouncements() {
  return useQuery({ queryKey: ['announcements'], queryFn: () => getJson<AnnouncementRow[]>('/api/announcements') });
}
export function useNotifications() {
  return useQuery({ queryKey: ['notifications'], queryFn: () => getJson<NotificationList>('/api/notifications'), refetchInterval: 60_000 });
}
export function useInbox() {
  return useQuery({ queryKey: ['inbox'], queryFn: () => getJson<InboxItem[]>('/api/inbox') });
}
export const announce = {
  create: (body: AnnouncementInput) => postJson<AnnouncementRow>('/api/announcements', body),
  approve: (id: string, version: number) => postJson<AnnouncementRow>(`/api/announcements/${encodeURIComponent(id)}/approve`, { version }),
  reject: (id: string, reason: string, version: number) => postJson<AnnouncementRow>(`/api/announcements/${encodeURIComponent(id)}/reject`, { reason, version }),
  janaza: (body: JanazaInput) => postJson<AnnouncementRow>('/api/announcements/janaza', body),
  markRead: (ids?: string[]) => postJson<{ marked: number }>('/api/notifications/read', ids ? { ids } : {}),
};

// ── Profile, settings, audit (pilot slice 11) ──
export function useProfile() {
  return useQuery({ queryKey: ['profile'], queryFn: () => getJson<MahalluProfile>('/api/settings/profile'), staleTime: 5 * 60_000 });
}
export function useAppSettings() {
  return useQuery({ queryKey: ['appSettings'], queryFn: () => getJson<AppSettings>('/api/settings/app') });
}
export function useAudit(filters: Record<string, string | number | undefined>) {
  return useQuery({ queryKey: ['audit', filters], queryFn: () => getJson<AuditList>(`/api/settings/audit${toQuery(filters)}`), placeholderData: keepPreviousData });
}
export const settings = {
  saveProfile: (body: ProfilePatch) => sendJson<MahalluProfile>('PUT', '/api/settings/profile', body),
  uploadMark: (file: File) => request<MahalluProfile>('/api/settings/mark', { method: 'PUT', headers: { 'Content-Type': file.type }, body: file }),
  removeMark: () => request<MahalluProfile>('/api/settings/mark', { method: 'DELETE' }),
  saveApp: (body: AppSettingsPatch) => sendJson<AppSettings>('PUT', '/api/settings/app', body),
};
/** Address of the mahallu's mark; the hash makes a new upload show at once. */
export const markUrl = (slug: string, hash: string) => `/api/public/marks/${encodeURIComponent(slug)}?v=${hash.slice(0, 12)}`;

// ── OptiFi console and mahallu links (ADR-0040) ──
export function useConsoleOverview() {
  return useQuery({ queryKey: ['console', 'overview'], queryFn: () => getJson<ConsoleOverview>('/api/platform/overview') });
}
export function useConsoleTenants() {
  return useQuery({ queryKey: ['console', 'tenants'], queryFn: () => getJson<ConsoleTenant[]>('/api/platform/tenants') });
}
export function useConsoleLeads() {
  return useQuery({ queryKey: ['console', 'leads'], queryFn: () => getJson<ConsoleLead[]>('/api/platform/leads') });
}
export function useConsoleOperators() {
  return useQuery({ queryKey: ['console', 'operators'], queryFn: () => getJson<ConsoleOperator[]>('/api/platform/operators') });
}
export function useConsoleAudit() {
  return useQuery({ queryKey: ['console', 'audit'], queryFn: () => getJson<ConsoleAuditRow[]>('/api/platform/audit') });
}
export function useConsoleMe(enabled: boolean) {
  return useQuery({ queryKey: ['console', 'me'], queryFn: () => getJson<ConsoleMe>('/api/platform/me'), enabled, retry: false });
}
export const consoleApi = {
  enter: () => postJson<SignInStepResponse>('/api/auth/console'),
  createTenant: (body: TenantCreateInput) => postJson<ConsoleTenant>('/api/platform/tenants', body),
  setStatus: (id: string, status: ConsoleTenant['status'], reason: string) => sendJson<ConsoleTenant>('PATCH', `/api/platform/tenants/${encodeURIComponent(id)}/status`, { status, reason }),
  setLead: (id: string, status: LeadStatus) => sendJson<{ ok: boolean }>('PATCH', `/api/platform/leads/${encodeURIComponent(id)}`, { status }),
};
export interface TenantCreateInput {
  nameEn: string;
  nameMl: string | null;
  slug: string;
  adminName: string;
  adminPhone: string;
}
export function usePublicMahallu(slug: string | null) {
  return useQuery({ queryKey: ['public-mahallu', slug], queryFn: () => getJson<PublicMahallu>(`/api/public/mahallus/${encodeURIComponent(slug!)}`), enabled: Boolean(slug), retry: false, staleTime: 300_000 });
}
